// src/contexts/ToastContext.js
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { TOAST_CONFIG, TOAST_STYLES, resolveNotificationColors } from '../config/notificationConfig';

const ToastContext = createContext(null);

let toastId = 0;
const generateId = () => `toast-${++toastId}`;

// Simple type-to-color mapping
const TOAST_TYPE_CONFIG = {
  success: resolveNotificationColors('success'),
  error: resolveNotificationColors('error'),
  warning: resolveNotificationColors('warning'),
  info: resolveNotificationColors('info') || resolveNotificationColors('default'),
};

// Individual Toast component
function Toast({ toast, onDismiss }) {
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const timerRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  const { id, type = 'info', title, message, duration = TOAST_CONFIG.duration, action } = toast;
  const colors = TOAST_TYPE_CONFIG[type] || TOAST_TYPE_CONFIG.info;

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onDismiss(id), 200);
  }, [id, onDismiss]);

  useEffect(() => {
    if (duration <= 0) return;
    const updateProgress = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        handleDismiss();
      } else {
        timerRef.current = requestAnimationFrame(updateProgress);
      }
    };
    timerRef.current = requestAnimationFrame(updateProgress);
    return () => {
      if (timerRef.current) cancelAnimationFrame(timerRef.current);
    };
  }, [duration, handleDismiss]);

  const handleMouseEnter = () => {
    if (timerRef.current) cancelAnimationFrame(timerRef.current);
  };

  const handleMouseLeave = () => {
    if (duration <= 0) return;
    startTimeRef.current = Date.now() - (duration * (100 - progress) / 100);
    const updateProgress = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        handleDismiss();
      } else {
        timerRef.current = requestAnimationFrame(updateProgress);
      }
    };
    timerRef.current = requestAnimationFrame(updateProgress);
  };

  return (
    <div
      role="alert"
      aria-live="polite"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`
        ${TOAST_STYLES.toast.base}
        ${colors.bg} ${colors.border}
        ${isExiting ? 'opacity-0 translate-x-2' : 'opacity-100 translate-x-0'}
        relative
      `}
    >
      <div className={TOAST_STYLES.toast.content}>
        <div className={TOAST_STYLES.toast.textWrapper}>
          {title && (
            <p className={`${TOAST_STYLES.toast.title} ${colors.text}`}>
              {title}
            </p>
          )}
          {message && (
            <p className={`${TOAST_STYLES.toast.message} text-gray-600 dark:text-gray-300`}>
              {message}
            </p>
          )}
          {action && (
            <button
              onClick={() => {
                action.onClick?.();
                handleDismiss();
              }}
              className="mt-2 text-sm font-medium text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary/50 rounded"
            >
              {action.label}
            </button>
          )}
        </div>
        <button
          onClick={handleDismiss}
          className={TOAST_STYLES.toast.closeBtn}
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        </button>
      </div>
      {duration > 0 && (
        <div
          className={`${TOAST_STYLES.toast.progress} ${colors.text.replace('text-', 'bg-')}`}
          style={{ transform: `scaleX(${progress / 100})` }}
        />
      )}
    </div>
  );
}

function ToastContainer({ toasts, onDismiss, position }) {
  const positionClass = TOAST_STYLES.positions[position] || TOAST_STYLES.positions['bottom-right'];
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className={`${TOAST_STYLES.container} ${positionClass}`} style={{ maxWidth: '100vw', padding: '0 1rem' }}>
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body
  );
}

export function ToastProvider({ children, position = TOAST_CONFIG.position, maxVisible = TOAST_CONFIG.maxVisible }) {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((options) => {
    const id = generateId();
    const newToast = { id, duration: TOAST_CONFIG.duration, ...options };
    setToasts((prev) => {
      const updated = [...prev, newToast];
      return updated.length > maxVisible ? updated.slice(-maxVisible) : updated;
    });
    return id;
  }, [maxVisible]);
  const dismissToast = useCallback((id) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);
  const dismissAll = useCallback(() => setToasts([]), []);
  const toast = useCallback((message, options = {}) => addToast({ message, ...options }), [addToast]);
  toast.success = useCallback((message, options = {}) => addToast({ type: 'success', message, ...options }), [addToast]);
  toast.error = useCallback((message, options = {}) => addToast({ type: 'error', message, ...options }), [addToast]);
  toast.warning = useCallback((message, options = {}) => addToast({ type: 'warning', message, ...options }), [addToast]);
  toast.info = useCallback((message, options = {}) => addToast({ type: 'info', message, ...options }), [addToast]);
  toast.dismiss = dismissToast;
  toast.dismissAll = dismissAll;
  const value = { toast, addToast, dismissToast, dismissAll, toasts };
  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} position={position} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context.toast;
}

export default ToastContext;
